<template>
  <div>
    <!-- Mode -->
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="text-muted small">
        <template v-if="mode === 'simple'">
          Answer three questions and the permissions and scope rules are generated for you.
        </template>
        <template v-else>
          Every resource, action and scope rule, set individually.
        </template>
      </div>

      <b-form-radio-group
        :checked="mode"
        buttons
        button-variant="outline-primary"
        size="sm"
        @change="onModeChange"
      >
        <b-form-radio value="simple">Simple</b-form-radio>
        <b-form-radio value="advanced">Advanced</b-form-radio>
      </b-form-radio-group>
    </div>

    <!-- Details -->
    <b-card no-body class="border mb-2">
      <b-card-header class="pb-1">
        <b-card-title class="mb-0">Client details</b-card-title>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Title *">
              <b-form-input
                v-model="form.title"
                placeholder="e.g. Pentecost University Integration"
                :disabled="disabled"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Contact name *">
              <b-form-input v-model="form.contact_name" :disabled="disabled" />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Contact email *">
              <b-form-input v-model="form.contact_email" type="email" :disabled="disabled" />
            </b-form-group>
          </b-col>

          <b-col v-if="isEdit" cols="12" md="6">
            <b-form-group label="Status">
              <v-select
                v-model="form.status"
                :options="statusOptions"
                :reduce="option => option.value"
                :clearable="false"
                :disabled="disabled"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Description">
              <b-form-textarea v-model="form.description" rows="2" :disabled="disabled" />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <guided-access-editor
      v-if="mode === 'simple'"
      :value="setup"
      :resources="registry.resources"
      :disabled="disabled"
      @input="onSetupChange"
    />

    <template v-else>
      <permissions-editor
        v-model="form.permissions"
        :resources="registry.resources"
        :disabled="disabled"
      />

      <scope-rules-editor
        v-model="form.scope_rules"
        :resources="registry.resources"
        :permissions="form.permissions"
        :disabled="disabled"
      />
    </template>

    <fixed-fields-editor
      :value="form.fixed_fields || []"
      :resources="registry.resources"
      :permissions="form.permissions"
      :scope-rules="form.scope_rules"
      :disabled="disabled"
      @input="onFixedFieldsChange"
    />

    <!-- Throughput -->
    <b-card v-if="mode === 'advanced'" no-body class="border mb-2">
      <b-card-header class="pb-1">
        <div>
          <b-card-title class="mb-25">Throughput</b-card-title>
          <b-card-text class="text-muted small mb-0">
            Sized for a partner backend serving many of its own end users, not a single
            caller. Defaults are deliberately higher than a per-user API key.
          </b-card-text>
        </div>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col cols="12" md="4">
            <b-form-group label="Requests per window">
              <b-form-input v-model.number="form.rate_limit.limit" type="number" min="1" :disabled="disabled" />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group label="Window (seconds)">
              <b-form-input v-model.number="form.rate_limit.window_seconds" type="number" min="1" :disabled="disabled" />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group label="Token lifetime (seconds)">
              <b-form-input v-model.number="form.token_ttl_seconds" type="number" min="60" :disabled="disabled" />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Network access and hidden fields (P12) -->
    <b-card no-body class="border mb-2">
      <b-card-header class="pb-1">
        <div>
          <b-card-title class="mb-25">Network access and hidden fields</b-card-title>
          <b-card-text class="text-muted small mb-0">
            Optional controls on top of the client's permissions.
          </b-card-text>
        </div>
      </b-card-header>

      <b-card-body>
        <b-form-group
          label="Allowed IP addresses"
          description="One per line: an address, an IPv4 range such as 203.0.113.0/24, or * for any address. Leave empty to allow any address."
        >
          <b-form-textarea v-model="form.allowed_ips" rows="3" :disabled="disabled" placeholder="Leave empty for any address" />
        </b-form-group>
        <b-alert variant="info" show class="small p-1">
          How strictly this list is applied is set on the server (<code>V2_IP_ALLOWLIST_MODE</code>:
          <b>log</b> only records calls it would refuse, <b>enforce</b> refuses them, <b>off</b> ignores lists).
        </b-alert>

        <h6 class="mt-2">Fields hidden from this client</h6>
        <p class="text-muted small">Removed from everything this client reads or gets back from a write.</p>
        <div v-for="(rule, index) in form.excluded_fields" :key="index" class="d-flex align-items-start mb-1">
          <v-select
            v-model="rule.resource"
            class="mr-1"
            style="min-width: 220px"
            :options="resourceOptions"
            :clearable="false"
            :disabled="disabled"
            placeholder="Resource"
          />
          <b-form-input v-model="rule.fields" :disabled="disabled" placeholder="Fields, comma separated e.g. ghana_card_number, bank_name" />
          <b-button v-if="!disabled" variant="flat-danger" class="ml-1" @click="form.excluded_fields.splice(index, 1)">
            <feather-icon icon="TrashIcon" />
          </b-button>
        </div>
        <b-button v-if="!disabled" size="sm" variant="outline-primary" @click="addExcluded">Hide fields</b-button>
      </b-card-body>
    </b-card>

    <!-- Danger zone: user provisioning -->
    <b-card no-body class="border border-danger mb-2">
      <b-card-header class="pb-1">
        <div>
          <b-card-title class="mb-25 text-danger">
            <feather-icon icon="AlertTriangleIcon" class="mr-50" />
            Account provisioning
          </b-card-title>
          <b-card-text class="text-muted small mb-0">
            Separate from permissions and scope: whether this client's writes may result
            in a login-capable account on PAC.
          </b-card-text>
        </div>
      </b-card-header>

      <b-card-body>
        <b-form-radio-group v-model="form.user_provisioning" stacked :disabled="disabled">
          <b-form-radio value="none" class="mb-1">
            <span class="font-weight-bold">Profile only</span>
            <span class="badge badge-light-success ml-50">Recommended</span>
            <div class="text-muted small">
              May create member records, never a login account. This is what partner
              institutions normally need.
            </div>
          </b-form-radio>

          <b-form-radio value="linked" class="mb-1">
            <span class="font-weight-bold">May link to existing accounts</span>
            <div class="text-muted small">
              May attach a profile to an account that already exists and falls inside
              this client's scope. Cannot create accounts.
            </div>
          </b-form-radio>

          <b-form-radio value="full">
            <span class="font-weight-bold text-danger">May create accounts</span>
            <div class="text-muted small">
              Lets this partner create login-capable accounts on PAC. Grant only to a
              party trusted with your authentication surface.
            </div>
          </b-form-radio>
        </b-form-radio-group>

        <b-alert v-if="form.user_provisioning === 'full'" variant="danger" show class="mt-1 mb-0">
          <div class="alert-body small">
            This client will be able to provision login accounts on PAC. Confirm this is
            intended before saving.
          </div>
        </b-alert>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import {
  BRow,
  BCol,
  BCard,
  BAlert,
  BButton,
  BCardBody,
  BCardText,
  BCardTitle,
  BCardHeader,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormTextarea,
  BFormRadioGroup,
} from 'bootstrap-vue'

import PermissionsEditor from './PermissionsEditor.vue'
import ScopeRulesEditor from './ScopeRulesEditor.vue'
import GuidedAccessEditor from './GuidedAccessEditor.vue'
import FixedFieldsEditor from './FixedFieldsEditor.vue'
import { DEFAULT_SETUP, buildSetup, inferSetup } from './clientSetupPresets'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BCard,
    BAlert,
    BCardBody,
    BCardText,
    BCardTitle,
    BCardHeader,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormTextarea,
    BFormRadioGroup,
    vSelect,
    PermissionsEditor,
    ScopeRulesEditor,
    GuidedAccessEditor,
    FixedFieldsEditor,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    registry: {
      type: Object,
      default: () => ({ resources: [], client_statuses: [] }),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Resolved once the registry lands — inferring a mode from an empty registry would
      // wrongly report every saved client as un-representable.
      mode: 'simple',
      mode_resolved: false,
      setup: { ...DEFAULT_SETUP },
    }
  },
  computed: {
    form: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    resourceOptions() {
      return (this.registry.resources || []).map(r => r.resource)
    },
    statusOptions() {
      const statuses = this.registry.client_statuses || ['active', 'suspended', 'revoked']
      return statuses.map(status => ({
        value: status,
        label: status.charAt(0).toUpperCase() + status.slice(1),
      }))
    },
  },
  watch: {
    'registry.resources': {
      handler(resources) {
        if (this.mode_resolved || !resources || !resources.length) return
        this.mode_resolved = true

        // Deferred a tick because the edit page assigns the registry and the loaded
        // client in one block: reading the form now could catch it still empty and
        // wrongly open a saved client in the simple view.
        this.$nextTick(() => this.resolveMode(resources))
      },
      immediate: true,
    },
  },
  methods: {
    addExcluded() {
      if (!Array.isArray(this.form.excluded_fields)) this.$set(this.form, 'excluded_fields', [])
      this.form.excluded_fields.push({ resource: this.resourceOptions[0] || '', fields: '' })
    },
    resolveMode(resources) {
      const inferred = inferSetup(resources, this.form)

      if (inferred) {
        this.setup = inferred
        this.mode = 'simple'
        return
      }

      // A saved client whose rules the simple view cannot express opens in advanced, so
      // nothing an admin configured by hand is hidden from them.
      const configured = (this.form.permissions || []).length
        || (this.form.scope_rules || []).length

      this.mode = configured ? 'advanced' : 'simple'
    },
    onFixedFieldsChange(fixed_fields) {
      this.$emit('input', { ...this.form, fixed_fields })
    },
    onSetupChange(setup) {
      this.setup = setup
      this.applySetup(setup)
    },
    /** The generated payload replaces permissions and scope_rules wholesale. */
    applySetup(setup) {
      const { permissions, scope_rules } = buildSetup(this.registry.resources, setup)
      this.$emit('input', { ...this.form, permissions, scope_rules })
    },
    async onModeChange(mode) {
      if (mode === this.mode) return

      if (mode === 'advanced') {
        this.mode = 'advanced'
        return
      }

      // Going back to simple regenerates from the two answers. If the current rules are
      // not something the simple view can represent, that discards them — so ask first
      // rather than quietly rewriting an admin's hand-tuned client.
      const inferred = inferSetup(this.registry.resources, this.form)

      if (inferred) {
        this.setup = inferred
        this.mode = 'simple'
        return
      }

      const confirmed = await this.$swal({
        title: 'Switch to the simple view?',
        text: 'This client has rules the simple view cannot show. Switching replaces them with the ones the simple view generates.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, replace them',
        cancelButtonText: 'Stay in advanced',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      if (!confirmed || !confirmed.value) return

      this.mode = 'simple'
      this.applySetup(this.setup)
    },
  },
}
</script>
